import { activeStatuses, insertRoll, type JanusCtx, type Row } from '../ability/context';
import type { JanusInput } from '../ability/janus';
import { applyStatusEffect } from '../ability/statuses/apply-status-effect';
import { removeStoredStatus } from '../ability/statuses/status-effect-removal-helpers';
import { statusItemId } from '../statuses/expiry';
import { applyDyingStacksToTarget } from '../port/k4-dispatch/apply_dying_stacks_to_target';
import { browserDependencies } from '../port/browser-dependencies';
import type { DispatchEnv } from '../port/k4-dispatch/dispatch';
import { composeActualDeath, dropDyingHands } from './compose';

import { DYING_STATES as DYING, RECOVERY_STATES as RECOVERY } from './state-ids';

/** Keep K4's normal/critical/recovery decision tree; adapt only its writes into this frame. */
export function resolveDyingStacks(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row, critical: boolean): void {
    const held = () => activeStatuses(ctx, target);
    const level = () => held().some(s => statusItemId(s) === DYING[2]) ? 2
        : held().some(s => DYING.slice(0, 2).includes(statusItemId(s))) ? 1 : 0;
    const deps = {
        has_status_effect: (_row: unknown, id: string) => held().some(s => statusItemId(s) === id),
        layer_value: (_row: unknown, key: string) => key === 'critical_hit' ? Number(critical) : null,
        drop_target_equipped_in_hand_inventory: () => dropDyingHands(ctx, target),
        remove_recovery_and_dying_states: () => {
            for (const status of [...held()]) if ([...DYING, ...RECOVERY].includes(statusItemId(status))) removeStoredStatus(ctx, target, status, 'single');
        },
        insert_log_message: (_roll: unknown, kind: string) => insertRoll(ctx, { roll_type_1: kind, by: target }),
        kill_creature: () => composeActualDeath(ctx, caster, target, ability, 0),
        apply_status_effect: (_connection: unknown, _caster: unknown, _target: unknown, _targets: unknown, _affected: unknown,
            _origin: unknown, item: string, _ends: unknown, _roll: unknown, log = true) => {
            let id = item;
            if (DYING.includes(item)) {
                const next = Math.min(2, level() + (item === DYING[2] ? 2 : 1));
                for (const status of [...held()]) if (DYING.includes(statusItemId(status))) removeStoredStatus(ctx, target, status, 'single');
                id = DYING[next]!;
            }
            applyStatusEffect(ctx, input, ability, id, 'status_effect_applied', target, target,
                { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, true, log, 0, false, '', false, true);
        },
    };
    applyDyingStacksToTarget({ value: target }, '', { item_layer_types: { critical_hit: Number(critical) } },
        { value: [] }, browserDependencies(deps) as unknown as DispatchEnv);
}
